// src/frontend/src/components/Stock.js
import MapChart from "./MapChart";
import StateAvailabilityPanel from "./StateAvailabilityPanel";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import styles from "./Stock.module.css";
import { fetchWithAuth } from "../utils/ApiUtils";
import { useAuth } from "../context/AuthContext";
import { AutobuyService } from "../AutobuyService";
import {
    Button, Typography, Box, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, CircularProgress, Tooltip
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CancelIcon from "@mui/icons-material/Cancel";
import { Link, useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

export const Stock = ({ stock }) => {
    // US states map URL
    const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

    const [stateData, setStateData] = useState([]);
    const [, setStateOverview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(Date.now());
    const { isAuthenticated, isPremium, login } = useAuth();
    const navigate = useNavigate();

    // Autobuy state
    const [hasAutobuy, setHasAutobuy] = useState(false);
    const [isAutobuyLoading, setIsAutobuyLoading] = useState(false);
    const [userCredits, setUserCredits] = useState(0);
    const [autobuyDialogOpen, setAutobuyDialogOpen] = useState(false);
    const [addCreditsDialogOpen, setAddCreditsDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Generate demo state data for non-premium preview
    const generateDemoStateData = useCallback(() => {
        return [
            { stateName: "California", stateCode: "CA", available: true, zipCode: "95814" },
            { stateName: "Texas", stateCode: "TX", available: true, zipCode: "78701" },
            { stateName: "Florida", stateCode: "FL", available: true, zipCode: "32301" },
            { stateName: "Washington", stateCode: "WA", available: true, zipCode: "98501" },
            { stateName: "Colorado", stateCode: "CO", available: true, zipCode: "80202" },
            { stateName: "Georgia", stateCode: "GA", available: true, zipCode: "30303" },
            { stateName: "Arizona", stateCode: "AZ", available: false, zipCode: "85001" },
            { stateName: "New York", stateCode: "NY", available: false, zipCode: "12207" }
        ];
    }, []);

    const fetchStateAvailability = useCallback(async () => {
        if (!stock || !stock.sku) return;

        try {
            setIsLoading(true);

            // Basic overview for all users
            const overviewResponse = await fetchWithAuth(`/api/v1/stocks/sku/${stock.sku}/states`);
            if (overviewResponse.ok) {
                const overviewData = await overviewResponse.json();
                setStateOverview(overviewData.data);
            }

            // Detailed data only for premium users
            if (isPremium && isAuthenticated) {
                const response = await fetchWithAuth(`/api/v1/stocks/sku/${stock.sku}/states/premium`);

                if (response.ok) {
                    const apiResponse = await response.json();
                    setStateData(apiResponse.data || []);
                    setLastUpdated(Date.now());
                } else {
                    setStateData([]);
                }
            } else {
                // For non-premium users, we don't load the actual data
                // But we'll keep the state array empty so the UI can handle it appropriately
                setStateData([]);
            }
        } catch (err) {
            console.error(`Error fetching state data: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [stock, isPremium, isAuthenticated]);

    // Check if stock is set for autobuy
    const checkAutobuyStatus = useCallback(async () => {
        if (!isAuthenticated || !stock || !stock.sku) return;

        try {
            setIsAutobuyLoading(true);
            const hasAutobuy = await AutobuyService.isStockSetForAutobuy(stock.sku);
            setHasAutobuy(hasAutobuy);

            // Get user credits
            const credits = await AutobuyService.getUserCredits();
            setUserCredits(credits);
        } catch (error) {
            console.error("Error checking autobuy status:", error);
        } finally {
            setIsAutobuyLoading(false);
        }
    }, [isAuthenticated, stock]);

    useEffect(() => {
        fetchStateAvailability();
        checkAutobuyStatus();

        const interval = setInterval(fetchStateAvailability, 30000);
        return () => clearInterval(interval);
    }, [fetchStateAvailability, checkAutobuyStatus]);

    // Handle autobuy operations
    const handleAutobuyButton = async () => {
        if (!isAuthenticated) {
            // Save current page as return destination
            localStorage.setItem('redirectAfterLogin', window.location.pathname);
            login();
            return;
        }

        if (!isPremium) {
            // Redirect to subscription instead of showing credits dialog
            navigate('/subscription');
            return;
        }

        if (hasAutobuy) {
            // Show confirmation dialog before cancelling
            setAutobuyDialogOpen(true);
        } else {
            // Check if they have credits
            if (userCredits <= 0) {
                // Redirect to credits page
                navigate('/credits');
            } else {
                // Show confirmation dialog before setting up autobuy
                setAutobuyDialogOpen(true);
            }
        }
    };

    const handleCloseDialog = () => {
        setAutobuyDialogOpen(false);
    };

    const handleCloseCreditsDialog = () => {
        setAddCreditsDialogOpen(false);
    };

    const handleConfirmAutobuy = async () => {
        try {
            setIsAutobuyLoading(true);

            if (hasAutobuy) {
                await AutobuyService.cancelAutobuy(stock.sku);
                setHasAutobuy(false);
                // Also update credits count here after successful cancellation
                const newCredits = await AutobuyService.getUserCredits();
                setUserCredits(newCredits);
                showSnackbar('Autobuy cancelled, credit refunded', 'success');
            } else {
                await AutobuyService.createAutobuy(stock.sku);
                setHasAutobuy(true);
                // Update credits count
                const newCredits = await AutobuyService.getUserCredits();
                setUserCredits(newCredits);
                showSnackbar('Autobuy created successfully', 'success');
            }

            handleCloseDialog();
        } catch (error) {
            console.error('Error with autobuy operation:', error);
            showSnackbar(error.message || 'Error with autobuy operation', 'error');
        } finally {
            setIsAutobuyLoading(false);
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    // Memoized map chart optimization
    const memoizedMapChart = useMemo(() => (
        <MapChart stateData={stateData} key={lastUpdated} />
    ), [stateData, lastUpdated]);

    // Map display depends on premium status
    const renderMap = useCallback(() => {
        if (isLoading) {
            return <div className={styles.loading}>Loading map data...</div>;
        }

        if (isPremium) {
            // Premium users see the full map - use memoized version
            return memoizedMapChart;
        } else {
            // Generate demo data for non-premium users
            const demoData = generateDemoStateData();

            // Non-premium users see a blurred teaser with upgrade option
            return (
                <Box sx={{
                    position: "relative",
                    p: 2,
                    height: 400, // Increased height to match premium version better
                    borderBottom: "1px solid #eee"
                }}>
                    <ComposableMap
                        projection="geoAlbersUsa"
                        projectionConfig={{
                            scale: 1000 // Adjusted scale for better view
                        }}
                        style={{
                            width: "100%",
                            height: "100%",
                            filter: "blur(1px)" // Slight blur effect to indicate locked content
                        }}
                    >
                        <Geographies geography={geoUrl}>
                            {({ geographies }) =>
                                geographies.map(geo => {
                                    const stateName = geo.properties.name;
                                    // Use demo data to show some states as available
                                    const stateInfo = demoData.find(s => s.stateName === stateName);
                                    const available = stateInfo ? stateInfo.available : false;

                                    return (
                                        <Geography
                                            key={geo.rsmKey}
                                            geography={geo}
                                            fill={available ? "#4da375" : "#a34d5d"}
                                            stroke="#FFFFFF"
                                            strokeWidth={0.5}
                                            style={{
                                                default: { outline: "none" },
                                                hover: {
                                                    fill: available ? "#6FD19A" : "#D16F79",
                                                    outline: "none"
                                                }
                                            }}
                                        />
                                    );
                                })
                            }
                        </Geographies>
                    </ComposableMap>

                    {/* Legend */}
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 10,
                            right: 10,
                            display: "flex",
                            bgcolor: "rgba(255,255,255,0.8)",
                            borderRadius: 1,
                            p: 1,
                            zIndex: 5
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                            <Box sx={{ width: 12, height: 12, bgcolor: "#4da375", borderRadius: 0.5, mr: 1 }} />
                            <Typography variant="caption">Available</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Box sx={{ width: 12, height: 12, bgcolor: "#a34d5d", borderRadius: 0.5, mr: 1 }} />
                            <Typography variant="caption">Unavailable</Typography>
                        </Box>
                    </Box>

                    {/* State count indicator */}
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            position: "absolute",
                            bottom: 10,
                            left: 10,
                            bgcolor: "rgba(255,255,255,0.8)",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            zIndex: 5
                        }}
                    >
                        Available in {demoData.filter(s => s.available).length} of 50 states
                    </Typography>

                    {/* Premium Overlay - Lock Icon & Text */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: "rgba(255,255,255,0.6)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <LockIcon sx={{ color: "text.secondary", mr: 1, fontSize: 20 }} />
                            <Typography variant="subtitle1" fontWeight="bold">
                                See State Availability
                            </Typography>
                        </Box>
                        {isAuthenticated ? (
                            <Button
                                variant="contained"
                                color="primary"
                                component={Link}
                                to="/subscription"
                                sx={{ mt: 2 }}
                            >
                                Upgrade to Premium
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => login()}
                                sx={{ mt: 2 }}
                            >
                                Sign in to Upgrade
                            </Button>
                        )}
                    </Box>
                </Box>
            );
        }
    }, [isLoading, isPremium, memoizedMapChart, generateDemoStateData, geoUrl, isAuthenticated, login]);

    // State list also depends on premium status
    const renderStateList = useCallback(() => {
        if (isPremium) {
            return <StateAvailabilityPanel stateData={stateData} />;
        } else {
            // Use demo data for non-premium preview
            const demoData = generateDemoStateData();
            const availableStates = demoData.filter(state => state.available);

            return (
                <div className={styles.panelContainer}>
                    <h4 className={styles.panelHeading}>Available Store Locations</h4>

                    {/* Location table header */}
                    <Box sx={{
                        display: "flex",
                        px: 2,
                        py: 1,
                        borderBottom: "1px solid #eee",
                        borderTop: "1px solid #eee",
                        bgcolor: "#f9f9f9"
                    }}>
                        <Typography
                            variant="body2"
                            fontWeight="500"
                            sx={{
                                flex: 1,
                                color: "text.secondary"
                            }}
                        >
                            State
                        </Typography>
                        <Typography
                            variant="body2"
                            fontWeight="500"
                            sx={{
                                flex: 1,
                                color: "text.secondary"
                            }}
                        >
                            Zip Code
                        </Typography>
                    </Box>

                    {/* Location rows with Premium Overlay */}
                    <Box sx={{
                        position: "relative",
                        height: "200px",
                        overflowY: "hidden"
                    }}>
                        {/* Blurred state list */}
                        <Box sx={{
                            px: 2,
                            filter: "blur(2px)",
                            opacity: 0.7
                        }}>
                            {availableStates.map(state => (
                                <Box
                                    key={state.stateName}
                                    sx={{
                                        display: "flex",
                                        py: 1.5,
                                        borderBottom: "1px solid #f5f5f5"
                                    }}
                                >
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                        {state.stateName}
                                    </Typography>
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                        {state.zipCode}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Premium Overlay - Lock Icon & Text */}
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                bgcolor: "rgba(255,255,255,0.7)",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 10
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <LockIcon sx={{ color: "text.secondary", mr: 1, fontSize: 20 }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    See Store Locations
                                </Typography>
                            </Box>
                            {isAuthenticated ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component={Link}
                                    to="/subscription"
                                    size="small"
                                    sx={{ mt: 2 }}
                                >
                                    Upgrade to Premium
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => login()}
                                    size="small"
                                    sx={{ mt: 2 }}
                                >
                                    Sign in to Upgrade
                                </Button>
                            )}
                        </Box>
                    </Box>
                </div>
            );
        }
    }, [isPremium, stateData, generateDemoStateData, isAuthenticated, login]);

    if (!stock) return null;

    const dateNow = new Date().toISOString().split("T")[0];
    const inStoreDiffInMs =
        new Date(dateNow) - new Date(stock.inStoreAvailabilityUpdateDate);
    const onlineDiffInMs =
        new Date(dateNow) - new Date(stock.onlineAvailabilityUpdateDate);
    const inStoreDiffInDays = Math.round(inStoreDiffInMs / (1000 * 60 * 60 * 24));
    const onlineDiffInDays = Math.round(onlineDiffInMs / (1000 * 60 * 60 * 24));

    let releaseDate = stock.releaseDate;
    if (stock.releaseDate === "2030-01-02" || stock.releaseDate === null) releaseDate = "Unknown";
    let skuText = "View Product";
    let skuLink = stock.url;

    return (
        <div className={styles.container}>
            <div className={styles.container_left}>
                <div className={styles.overview_panel}>
                    <img src={stock.image} alt={stock.alternateViewsImage} />

                    <h1 className={styles.overview_name}>{stock.name}</h1>
                    <div className={styles.overview_region}>{stock.sku}</div>

                    {/* Autobuy Button */}
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Tooltip title={isAuthenticated ?
                            (hasAutobuy ? "Cancel Autobuy" :
                                userCredits > 0 ? "Automatically add to cart when available" : "Purchase autobuy credits")
                            : "Sign in to use Autobuy"}>
                            <span>
                                <Button
                                    variant={hasAutobuy ? "outlined" : "contained"}
                                    color={hasAutobuy ? "secondary" : "primary"}
                                    onClick={handleAutobuyButton}
                                    startIcon={hasAutobuy ? <CancelIcon /> : <ShoppingCartIcon />}
                                    disabled={isAutobuyLoading}
                                    fullWidth
                                >
                                    {isAutobuyLoading ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        hasAutobuy ? "Cancel Autobuy" : "Autobuy"
                                    )}
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>

                    {/* Credits display for logged-in users */}
                    {isAuthenticated && (
                        <Box sx={{ mt: 1, mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Autobuy Credits: {userCredits}
                                {userCredits === 0 && (
                                    <Button
                                        variant="text"
                                        color="primary"
                                        size="small"
                                        onClick={() => setAddCreditsDialogOpen(true)}
                                        sx={{ ml: 1 }}
                                    >
                                        Add More
                                    </Button>
                                )}
                            </Typography>
                        </Box>
                    )}

                    <div className={styles.overview_numbers}>
                        <div className={styles.overview_population}>
                            <div className={styles.overview_value}>
                                {stock.inStoreAvailability ? (
                                    <p className={styles.in_stock}>Available</p>
                                ) : (
                                    <p className={styles.out_of_stock}>Unavailable</p>
                                )}
                            </div>
                            <div className={styles.overview_label}>
                                In Store Availability
                            </div>
                        </div>

                        <div className={styles.overview_area}>
                            <div className={styles.overview_value}>
                                {stock.onlineAvailability ? (
                                    <p className={styles.in_stock}>Available</p>
                                ) : (
                                    <p className={styles.out_of_stock}>Unavailable</p>
                                )}
                            </div>
                            <div className={styles.overview_label}>Online Availability</div>
                        </div>
                    </div>
                </div>
                {renderStateList()}
            </div>
            <div className={styles.container_right}>
                <div className={styles.details_panel}>
                    <h4 className={styles.details_panel_heading}>Details</h4>

                    <div className={styles.details_panel_row}>
                        <div className={styles.details_panel_label}>Price</div>
                        <div className={styles.details_panel_value}>
                            ${stock.regularPrice}
                        </div>
                    </div>

                    <div className={styles.details_panel_row}>
                        <div className={styles.details_panel_label}>Release date</div>
                        <div className={styles.details_panel_value}>{releaseDate}</div>
                    </div>

                    <div className={styles.details_panel_row}>
                        <div className={styles.details_panel_label}>
                            Updated in store
                        </div>
                        <div className={styles.details_panel_value}>
                            {inStoreDiffInDays} days ago
                        </div>
                    </div>

                    <div className={styles.details_panel_row}>
                        <div className={styles.details_panel_label}>
                            Updated online
                        </div>
                        <div className={styles.details_panel_value}>
                            {onlineDiffInDays} days ago
                        </div>
                    </div>

                    <div className={styles.details_panel_row}>
                        <div className={styles.details_panel_label}>Link</div>
                        <div className={styles.details_panel_value}>
                            <a target="_blank" rel="noreferrer" href={skuLink}>
                                {skuText}
                            </a>
                        </div>
                    </div>
                </div>
                <div className={styles.details_panel}>
                    <h4 className={styles.details_panel_heading}>State Availability</h4>
                    {renderMap()}
                </div>
            </div>

            {/* Autobuy Confirmation Dialog */}
            <Dialog
                open={autobuyDialogOpen}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {hasAutobuy ? "Cancel Autobuy" : "Confirm Autobuy"}
                </DialogTitle>
                <DialogContent>
                    {hasAutobuy ? (
                        <DialogContentText>
                            Are you sure you want to cancel the autobuy for this GPU? This will remove it from your autobuy list and you'll need to use another credit to set it up again.
                        </DialogContentText>
                    ) : (
                        <DialogContentText>
                            Set up autobuy for this GPU? This will use 1 autobuy credit. We'll automatically add this item to your cart as soon as it becomes available and notify you via email.
                            <br /><br />
                            <strong>You have {userCredits} credit{userCredits !== 1 ? 's' : ''} remaining.</strong>
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleConfirmAutobuy}
                        variant="contained"
                        color={hasAutobuy ? "error" : "primary"}
                        disabled={isAutobuyLoading}
                    >
                        {isAutobuyLoading ? (
                            <CircularProgress size={24} />
                        ) : (
                            hasAutobuy ? "Confirm Cancel" : "Confirm Autobuy"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Credits Dialog */}
            <Dialog
                open={addCreditsDialogOpen}
                onClose={handleCloseCreditsDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {isPremium ? "Purchase Autobuy Credits" : "Premium Subscription Required"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {isPremium ? (
                            "You don't have any autobuy credits remaining. Premium members receive 1 free credit each month with their subscription. You can purchase additional credits on the credits page."
                        ) : (
                            "You need a premium subscription to use autobuy credits. Subscribe to get 1 free credit each month and the ability to purchase more."
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreditsDialog}>Cancel</Button>
                    <Button
                        component={Link}
                        to={isPremium ? "/credits" : "/subscription"}
                        variant="contained"
                        color="primary"
                        onClick={handleCloseCreditsDialog}
                    >
                        {isPremium ? "Go to Credits Page" : "View Subscription Plans"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};